import { forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, RotateCw, Stamp, FileCheck2, RotateCcw } from "lucide-react";
import { TRANSLATIONS, TONE_COLORS } from "@/i18n";
import { TOP_MODE_COLORS } from "@/constants/diplomate";
import {
  FADE_IN_INITIAL,
  FADE_IN_ANIMATE,
  FADE_OUT_EXIT,
  SLIDE_UP_INITIAL,
  SLIDE_UP_ANIMATE,
  SLIDE_UP_TRANSITION,
} from "@/constants/diplomate";

/**
 * OutputPanel — text-based output for CORDIALEMENT and REVERSE.
 * ANTIDOTE and BULLSHIT SCORE use their own structured panels.
 */
export const OutputPanel = forwardRef(function OutputPanel(
  { lang, topMode, tone, output, loading, copied, onCopy, onRegenerate },
  ref,
) {
  const t = TRANSLATIONS[lang];
  const isReverse = topMode === "reverse";
  const accent = isReverse ? TOP_MODE_COLORS.reverse : TONE_COLORS[tone] || "#B5EAD7";
  const tinted = output ? `${accent}22` : "#FFFFFF";
  const labelText = isReverse ? t.reverseResultLabel : t.outputLabel;
  const emptyText = isReverse ? t.reverseOutputEmpty : t.outputEmpty;
  const HeaderIcon = isReverse ? RotateCcw : FileCheck2;
  const statusText = isReverse ? "" : t.outputStatus;

  return (
    <div
      ref={ref}
      data-testid="output-panel"
      className="border-2 border-black nb-shadow relative flex flex-col"
      style={{ background: tinted }}
    >
      <div className="flex items-center justify-between border-b-2 border-black px-5 py-3 bg-[#F3EFE7]">
        <div className="flex items-center gap-2">
          <HeaderIcon className="w-3.5 h-3.5 text-black" />
          <span className="font-mono text-[11px] uppercase tracking-widest font-bold">
            {labelText}
          </span>
        </div>
        {output && !loading && (
          <div className="flex items-center gap-2">
            <button
              data-testid="regenerate-btn"
              onClick={onRegenerate}
              disabled={loading}
              className="bg-white border-2 border-black nb-shadow-sm nb-hover px-2.5 py-1 font-bold text-[11px] uppercase tracking-widest flex items-center gap-1.5"
            >
              <RotateCw className="w-3 h-3" /> {t.again}
            </button>
            <button
              data-testid="copy-btn"
              onClick={() => onCopy()}
              className="bg-white border-2 border-black nb-shadow-sm nb-hover px-2.5 py-1 font-bold text-[11px] uppercase tracking-widest flex items-center gap-1.5"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" /> {t.copied}
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" /> {t.copy}
                </>
              )}
            </button>
          </div>
        )}
      </div>
      <div className="min-h-[220px] sm:min-h-[280px] relative px-5 py-5">
        <AnimatePresence mode="wait">
          {loading && <LoadingState key="loading" t={t} />}
          {!loading && output && (
            <motion.div
              key="output"
              initial={SLIDE_UP_INITIAL}
              animate={SLIDE_UP_ANIMATE}
              transition={SLIDE_UP_TRANSITION}
              className="flex flex-col gap-4"
            >
              {statusText && (
                <div className="flex items-center gap-2 self-start admin-stamp">
                  <Stamp className="w-3 h-3" strokeWidth={2.5} />
                  <span className="text-[10px] font-bold" data-testid="output-status">
                    {statusText}
                  </span>
                </div>
              )}
              <div
                data-testid="output-text"
                className="whitespace-pre-wrap text-base sm:text-[17px] leading-[1.65] text-black"
              >
                {output}
              </div>
            </motion.div>
          )}
          {!loading && !output && <EmptyState key="empty" msg={emptyText} />}
        </AnimatePresence>
      </div>
    </div>
  );
});

const LoadingState = ({ t }) => (
  <motion.div
    initial={FADE_IN_INITIAL}
    animate={FADE_IN_ANIMATE}
    exit={FADE_OUT_EXIT}
    className="absolute inset-0 flex flex-col items-center justify-center gap-3"
  >
    <div className="flex gap-2">
      <span className="w-2.5 h-2.5 bg-black animate-pulse-dot" style={{ animationDelay: "0s" }} />
      <span className="w-2.5 h-2.5 bg-black animate-pulse-dot" style={{ animationDelay: "0.15s" }} />
      <span className="w-2.5 h-2.5 bg-black animate-pulse-dot" style={{ animationDelay: "0.3s" }} />
    </div>
    <p className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
      {t.generating}
    </p>
  </motion.div>
);

const EmptyState = ({ msg }) => (
  <motion.div
    initial={FADE_IN_INITIAL}
    animate={FADE_IN_ANIMATE}
    className="absolute inset-0 flex items-center justify-center text-center px-6"
  >
    <p className="text-[#4A4A4A] text-sm italic max-w-xs leading-relaxed">{msg}</p>
  </motion.div>
);
